import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError, catchError, retry, timer, tap } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { HTTP_ERROR_TYPE } from './constants';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/services/auth.service';


/**
 * 网络错误拦截器
 * 处理HTTP请求中的网络错误，并提供重试机制和友好的错误提示
 */
let isOtherDeviceLoginModalShowing = false;
let isLoginModalShowing = false;

export function NetworkErrorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const nzModalService = inject(NzModalService);
  const nzMessageService = inject(NzMessageService);
  const translateService = inject(TranslateService);
  const authService = inject(AuthService);
  // 最大重试次数
  const maxRetries = 3;
  // 重试间隔时间(ms)
  const retryDelay = 1000;

  // 只对GET请求进行重试
  const shouldRetry = req.method === 'GET';

  let handle$ = next(req);
  let errorType: string;

  // 如果是GET请求，则添加重试逻辑
  // if (shouldRetry) {
  //   handle$ = handle$.pipe(
  //     retry({
  //       count: maxRetries,
  //       delay: (error, retryCount) => {
  //         // 只对网络错误进行重试
  //         if (isNetworkError(error)) {
  //           console.warn(`请求失败，正在进行第 ${retryCount} 次重试...`);
  //           return timer(retryDelay * Math.pow(2, retryCount - 1)); // 指数退避策略
  //         }
  //         return throwError(() => error);
  //       }
  //     })
  //   );
  // }



  // 将服务传递给辅助函数
  const handleError = (error: HttpErrorResponse, request: HttpRequest<unknown>) => {
    errorType = getErrorType(error);
    const errorMessage = getErrorMessage(error, request);
    showUserErrorMessage(errorMessage);
  };

  const isNetworkError = (error: HttpErrorResponse): boolean => {
    return error.status === 0
      && !error.ok
      && (error.error instanceof ProgressEvent || !error.url);
  };

  const getErrorMessage = (error: HttpErrorResponse, request: HttpRequest<unknown>): string => {
    const method = request.method;
    const url = request.url;

    switch (errorType) {
      case HTTP_ERROR_TYPE.CORS_ERROR:
        console.log('CORS_ERROR', `[${new Date().toISOString()}] 请求: ${method} ${url}`, error)
        return translateService.instant('NetworkErrorType.CORS_ERROR');
      case HTTP_ERROR_TYPE.NETWORK_ERROR:
        console.log('NETWORK_ERROR', `[${new Date().toISOString()}] 请求: ${method} ${url}`, error)
        return translateService.instant('NetworkErrorType.NETWORK_ERROR');
      case HTTP_ERROR_TYPE.BAD_REQUEST:
        console.log('BAD_REQUEST', `[${new Date().toISOString()}] 请求: ${method} ${url},无效的请求参数`, error)
        return translateService.instant('NetworkErrorType.BAD_REQUEST', { message: error.message || translateService.instant('NetworkErrorType.INVALID_REQUEST_PARAMS') });
      case HTTP_ERROR_TYPE.UNAUTHORIZED:
        console.log('UNAUTHORIZED', `[${new Date().toISOString()}] 请求: ${method} ${url},未授权`, error)
        return translateService.instant('NetworkErrorType.UNAUTHORIZED');
      case HTTP_ERROR_TYPE.FORBIDDEN:
        console.log('FORBIDDEN', `[${new Date().toISOString()}] 请求: ${method} ${url},权限不足`, error)
        return translateService.instant('NetworkErrorType.FORBIDDEN');
      case HTTP_ERROR_TYPE.NOT_FOUND:
        console.log('NOT_FOUND', `[${new Date().toISOString()}] 请求: ${method} ${url},资源不存在`, error)
        return translateService.instant('NetworkErrorType.NOT_FOUND');
      case HTTP_ERROR_TYPE.SERVER_ERROR:
        console.log('SERVER_ERROR', `[${new Date().toISOString()}] 请求: ${method} ${url},服务器错误`, error)
        return translateService.instant('NetworkErrorType.SERVER_ERROR');
      case 'ignore':
        return '';
      default:
        console.log('UNKNOWN_ERROR', `[${new Date().toISOString()}] 请求: ${method} ${url},未知错误`, error)
        return translateService.instant('NetworkErrorType.UNKNOWN_ERROR');
    }
  };

  const showUserErrorMessage = (errorMessage: string): void => {
    switch (errorType) {
      case HTTP_ERROR_TYPE.UNAUTHORIZED:
        console.log('UNAUTHORIZED', errorMessage)
        if (isLoginModalShowing) {
          return;
        }
        isLoginModalShowing = true;
        
        const timer = setTimeout(() => {
          clearTimeout(timer);
          translateService.get('NetworkErrorType.NOT_LOGGED_IN').subscribe((res) => {
            nzModalService.create({
              nzTitle: res,
              nzContent: translateService.instant('NetworkErrorType.NOT_LOGGED_IN_MSG'),
              nzOkText: translateService.instant('Login'),
              nzOnOk: () => {
                authService.logout();
                isLoginModalShowing = false;
                window.location.href = '/dashboard/#/login';
              }
            });
          });
        }, 0);
        break;
      case 'ignore':
        break;
      default:
        nzMessageService.error(errorMessage, {
          nzDuration: 5000
        });
    }
  };

  const getErrorType = (error: HttpErrorResponse): string => {
    if (isNetworkError(error)) {
      if (error.url && error.url.includes('://')) {
        return HTTP_ERROR_TYPE.CORS_ERROR;
      }
      return HTTP_ERROR_TYPE.NETWORK_ERROR;
    }

    switch (error.status) {
      case 400:
        return HTTP_ERROR_TYPE.BAD_REQUEST;
      case 401:
        return HTTP_ERROR_TYPE.UNAUTHORIZED;
      case 403:
        return HTTP_ERROR_TYPE.FORBIDDEN;
      case 404:
        return HTTP_ERROR_TYPE.NOT_FOUND;
      case 500:
      case 502:
      case 503:
        return HTTP_ERROR_TYPE.SERVER_ERROR;
      case 9999999:
        return 'ignore';
      default:
        return HTTP_ERROR_TYPE.UNKNOWN_ERROR;
    }
  };

  return handle$.pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse && event.body) {
        const body = event.body as any;
        if (body.code == 301) {
          if (!isOtherDeviceLoginModalShowing) {
            isOtherDeviceLoginModalShowing = true;
            const timer = setTimeout(() => {
              clearTimeout(timer);
              nzModalService.warning({
                nzTitle: translateService.instant('NetworkErrorType.LOGIN_CONFLICT_ALERT_TITLE'),
                nzContent: '<span style="color: #d93026; font-size: 14px;">' + translateService.instant('NetworkErrorType.LOGIN_CONFLICT_ALERT_MESSAGE') + '</span>',
                nzOkText: translateService.instant('CONFIRM'),
                nzClosable: false,
                nzOnOk: () => {
                  isOtherDeviceLoginModalShowing = false;
                  localStorage.clear();
                  window.open("/dashboard/#/login", '_self');
                }
              });
            }, 1000);
          }
          const errorResponse = new HttpErrorResponse({
            status: 9999999, // 真实HTTP状态码
            statusText: 'Business Redirect',
            url: event.url || '',
          });
          throw errorResponse;
        } else {
          return event;
        }
      } else {
        return event;
      }
    }),
    catchError((error: HttpErrorResponse) => {
      handleError(error, req);
      return throwError(() => error);
    })
  );
}